import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type CustomerType = 'private' | 'business';

interface CustomerTypeContextValue {
  customerType: CustomerType | null;
  hasSelectedCustomerType: boolean;
  setCustomerType: (type: CustomerType) => void;
  clearCustomerType: () => void;
  vatLabel: string;
}

const STORAGE_KEY = 'customerType';

const CustomerTypeContext = createContext<CustomerTypeContextValue | undefined>(undefined);

export const CustomerTypeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customerType, setCustomerTypeState] = useState<CustomerType | null>(null);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'private' || stored === 'business') {
        setCustomerTypeState(stored);
      }
    } catch (error) {
      console.error('Could not read customerType from localStorage:', error);
    }
  }, []);

  const setCustomerType = (type: CustomerType) => {
    setCustomerTypeState(type);
    try {
      localStorage.setItem(STORAGE_KEY, type);
    } catch (error) {
      console.error('Could not write customerType to localStorage:', error);
    }
  };

  const clearCustomerType = () => {
    setCustomerTypeState(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Could not clear customerType in localStorage:', error);
    }
  };

  const value = useMemo<CustomerTypeContextValue>(() => {
    const vatLabel = customerType === 'business' ? 'exkl. moms' : 'inkl. moms';
    return {
      customerType,
      hasSelectedCustomerType: customerType !== null,
      setCustomerType,
      clearCustomerType,
      vatLabel
    };
  }, [customerType]);

  return <CustomerTypeContext.Provider value={value}>{children}</CustomerTypeContext.Provider>;
};

export const useCustomerType = (): CustomerTypeContextValue => {
  const context = useContext(CustomerTypeContext);

  // Safe fallback so UI never crashes if used outside provider.
  if (!context) {
    return {
      customerType: null,
      hasSelectedCustomerType: false,
      setCustomerType: () => undefined,
      clearCustomerType: () => undefined,
      vatLabel: 'inkl. moms'
    };
  }

  return context;
};

